/**
 * Pure edits on the room itself: dimensions, ceiling, corner chamfers.
 * Every function is `(room, args) => room` - new room, minimal new objects,
 * clamped internally so the result is always valid.
 */
import { cornerChamferMax, minLength, minWidth, roundToHalfInch } from '../constraints';
import { normalizeDegrees, type RoomPlacement } from '../geometry';
import type { CornerName, FloorPlanRoom } from '../schemas';
import { chamferAfterEdit } from './chamferLegs';
import { updateCorner } from './structural';

/** Set the room width, floored at the smallest width its edge content allows. */
export function setRoomWidth(room: FloorPlanRoom, width: number): FloorPlanRoom {
  const w = Math.max(minWidth(room), roundToHalfInch(width));
  return w === room.width ? room : { ...room, width: w };
}

/** Set the room length, floored at the smallest length its edge content allows. */
export function setRoomLength(room: FloorPlanRoom, length: number): FloorPlanRoom {
  const l = Math.max(minLength(room), roundToHalfInch(length));
  return l === room.length ? room : { ...room, length: l };
}

/** Set the ceiling height (≥ 0). */
export function setCeilingHeight(room: FloorPlanRoom, height: number): FloorPlanRoom {
  const h = Math.max(0, roundToHalfInch(height));
  return h === room.height ? room : { ...room, height: h };
}

/** Set the room's default floor type - the material of any floor not covered by a flooring region. */
export function setRoomFloorType(room: FloorPlanRoom, floorType: string): FloorPlanRoom {
  return floorType === room.floorType ? room : { ...room, floorType };
}

/**
 * Set one or both legs of a room-corner chamfer, clamped to the precomputed
 * max (so the chamfer can't hit an alcove or the opposite corner).
 */
export function setRoomCornerChamfer(
  room: FloorPlanRoom,
  corner: CornerName,
  legs: { dx?: number; dy?: number },
): FloorPlanRoom {
  const maxLegs = cornerChamferMax(room, corner);
  return updateCorner(room, corner, current => chamferAfterEdit(current, legs, maxLegs));
}

/**
 * Set the room's whole-property placement (the assembler's one edit): any
 * subset of level / x / y / z / rotation; fields not in the patch are left as
 * they are. Level is rounded to an integer, x / y / z to the half inch;
 * rotation is normalized into [0, 360). Returns the same room when nothing
 * changes.
 */
export function setRoomPlacement(room: FloorPlanRoom, patch: Partial<RoomPlacement>): FloorPlanRoom {
  const next: Partial<RoomPlacement> = {};
  if (patch.level != null && Math.round(patch.level) !== room.level) next.level = Math.round(patch.level);
  for (const axis of ['x', 'y', 'z'] as const) {
    if (patch[axis] == null) continue;
    const value = roundToHalfInch(patch[axis]);
    if (value !== room[axis]) next[axis] = value;
  }
  if (patch.rotation != null) {
    const rotation = normalizeDegrees(patch.rotation);
    if (rotation !== room.rotation) next.rotation = rotation;
  }
  return Object.keys(next).length ? { ...room, ...next } : room;
}
